import { useEffect, useMemo, useRef, useState } from "react";
import {
  OFFER_ICON_VIEWBOX,
  offerIconShapes,
  type OfferIconShape,
} from "@shared/offerIcons";
import type { GuestOffer, GuestOfferSlot } from "@shared/types/guest";
import type { Money } from "@shared/types/roomOffer";
import type { GuestRequest } from "@shared/types/guestRequest";
import type { Translate, UiKey } from "@shared/guest-strings";
import { cancelOfferRequest, fetchMyRequests, requestOffer } from "./api";
import { track } from "./analytics";
import type { Localize } from "./components";

/**
 * Room offers on the guest's phone (`POS-13`).
 *
 * The companion's own renderer for the catalogue the television shows. It draws
 * the SAME resolved feed - the server projects `TvRoomOffer` into
 * `GuestContent.offers`, so what is on sale, what it costs and which hours are
 * left cannot differ between the screen on the wall and the phone in the hand.
 *
 * ## Three kinds, one card
 *
 * A promotion is READ-ONLY here exactly as it is on the television: it is the
 * degenerate case of the same row (`docs/room-offers.md` §2) and there is
 * nothing to confirm. The server refuses one anyway
 * (`promotion_not_requestable`); `requestable` is what stops the button being
 * drawn in the first place.
 *
 * ## Charge to room, and no card
 *
 * The demo is charge-to-room only and **no card provider is registered**, so
 * there is no payment step and no payment-method picker - one option is not a
 * choice. `cardPayment.available` from the resolve payload is the seam's own
 * honest report; while it is false this surface never mentions a card at all,
 * because a disabled button is a promise nobody can keep.
 *
 * ## Nothing here waits for news
 *
 * A guest phone has no push channel: the companion session is a stateless
 * 12-hour cookie with no subscription record. So the card shows the row the
 * guest just created, plus whatever `requests/mine` returned when the page
 * loaded, and never polls. The realtime nudge goes the other way - the
 * TELEVISION in the same room updates itself when an order is placed here.
 */

/* -------------------------------------------------------------------------- */
/* The icon renderer - one of four, all reading the same shared geometry       */
/* -------------------------------------------------------------------------- */

/**
 * The companion's copy of the offer-icon renderer.
 *
 * The geometry lives in `@shared/offerIcons` and is defined exactly once; this
 * is the small piece of JSX that turns it into SVG for this surface. It is not
 * shared with the CMS's `OfferIcon.tsx` because `src/shared/` is compiled by
 * three tsconfigs and only one has a `jsx` option - a component there breaks
 * both CI build jobs ([[shared-alias-build-lanes]]).
 */
function OfferIcon(props: { name?: string; size?: number }) {
  const size = props.size ?? 22;
  const line = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const draw = (shape: OfferIconShape, i: number) => {
    switch (shape.kind) {
      case "rect":
        return (
          <rect
            key={i}
            x={shape.x}
            y={shape.y}
            width={shape.width}
            height={shape.height}
            rx={shape.rx}
            {...line}
          />
        );
      case "circle":
        return (
          <circle key={i} cx={shape.cx} cy={shape.cy} r={shape.r} {...line} />
        );
      case "dot":
        // The one FILLED primitive - see `OfferIconShape`.
        return (
          <circle
            key={i}
            cx={shape.cx}
            cy={shape.cy}
            r={shape.r}
            fill="currentColor"
            stroke="none"
          />
        );
      default:
        return <path key={i} d={shape.d} {...line} />;
    }
  };
  return (
    <svg
      className="offer__icon"
      width={size}
      height={size}
      viewBox={OFFER_ICON_VIEWBOX}
      aria-hidden="true"
    >
      {offerIconShapes(props.name).map(draw)}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Money and hours                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A price, as the guest reads it.
 *
 * Grouped by hand rather than through `Intl.NumberFormat`, for the same reason
 * the television's `formatMoney` is: `currency` is whatever the operator typed
 * ("kr", "€"), so it follows the amount rather than being placed by rule. Not
 * imported from `src/tv/` - the companion is its own bundle, and reaching into
 * the TV's scheme layer for six lines would drag the whole of it along.
 */
function formatMoney(price: Money, locale: string): string {
  const whole = Number.isInteger(price.amount);
  const fixed = Math.abs(price.amount).toFixed(whole ? 0 : 2);
  const [int, frac] = fixed.split(".");
  const decimal = locale === "en" ? "." : ",";
  // Non-breaking spaces, written as escapes so the intent survives a
  // copy-paste: a price must never wrap between its thousands, nor away from
  // its currency, in the middle of a card.
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
  const amount = frac ? `${grouped}${decimal}${frac}` : grouped;
  return `${price.amount < 0 ? "−" : ""}${amount}\u00a0${price.currency}`;
}

/** Add up what the guest picked. `null` is FREE - an amenity, not a zero-priced
 *  good - and a mixed-currency basket is refused by the server, so it simply
 *  draws no total rather than inventing one. */
function totalOf(
  offer: GuestOffer,
  quantities: Record<string, number>
): Money | null {
  const parts: Money[] = [];
  if (offer.price) parts.push(offer.price);
  for (const item of offer.items) {
    const qty = quantities[item.id] ?? 0;
    if (qty > 0 && item.price) {
      parts.push({
        amount: item.price.amount * qty,
        currency: item.price.currency,
      });
    }
  }
  if (!parts.length) return null;
  const currency = parts[0].currency;
  if (parts.some((p) => p.currency !== currency)) return null;
  return {
    amount: parts.reduce((sum, p) => sum + p.amount, 0),
    currency,
  };
}

/** Group the hours by their own `date` string. Never by parsing `start` into a
 *  `Date`: a slot start is a premise-local wall clock, and a phone in another
 *  zone would shift every one of them. */
function byDate(slots: GuestOfferSlot[]): Array<[string, GuestOfferSlot[]]> {
  const map = new Map<string, GuestOfferSlot[]>();
  for (const slot of slots) {
    const list = map.get(slot.date);
    if (list) list.push(slot);
    else map.set(slot.date, [slot]);
  }
  return [...map.entries()];
}

const STATUS_KEY: Record<GuestRequest["status"], UiKey> = {
  pending: "offer_status_pending",
  accepted: "offer_status_accepted",
  completed: "offer_status_completed",
  declined: "offer_status_declined",
  cancelled: "offer_status_cancelled",
  refunded: "offer_status_refunded",
};

/** A refusal code, as a sentence. Unknown codes fall back to the generic line:
 *  the closed set lives on the server and this must not go blank when it
 *  grows. */
function errorKey(code: string): UiKey {
  if (code === "slot_taken" || code === "slot_unavailable")
    return "offer_err_slot_taken";
  if (code === "offer_unavailable" || code === "checked_out")
    return "offer_err_unavailable";
  return "offer_err_generic";
}

/** One key per confirm ACTION, reused across its own retries. A key minted per
 *  attempt is the same as having none. */
function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/* -------------------------------------------------------------------------- */
/* The section                                                                */
/* -------------------------------------------------------------------------- */

export function OffersSection(props: {
  offers: GuestOffer[];
  /** False for a property-scope session: no room, so nothing to deliver to. */
  canOrder: boolean;
  /** From `?offer=` on the landing URL - the one companion URL definition. */
  focusOfferId: string | null;
  L: Localize;
  t: Translate;
  locale: string;
}) {
  const { offers, canOrder, focusOfferId, L, t, locale } = props;
  const [requests, setRequests] = useState<GuestRequest[]>([]);
  const [openId, setOpenId] = useState<string | null>(focusOfferId);
  const focused = useRef(false);

  // One fetch, on mount. Not a poll: there is no channel to this phone, and a
  // timer here would only look like one.
  useEffect(() => {
    if (!canOrder) return;
    let live = true;
    fetchMyRequests().then((rows) => {
      if (live) setRequests(rows);
    });
    return () => {
      live = false;
    };
  }, [canOrder]);

  const scrollTo = (el: HTMLElement | null) => {
    if (!el || focused.current || !focusOfferId) return;
    focused.current = true;
    el.scrollIntoView({ block: "center" });
  };

  if (!offers.length) return null;

  return (
    <>
      <h2 className="group">{t("offers")}</h2>
      {offers.map((offer) => (
        <OfferCard
          key={offer.id}
          offer={offer}
          canOrder={canOrder}
          open={openId === offer.id}
          onToggle={() =>
            setOpenId((id) => (id === offer.id ? null : offer.id))
          }
          requests={requests.filter((r) => r.offerId === offer.id)}
          onRequestChanged={(row) =>
            setRequests((rows) => [row, ...rows.filter((r) => r.id !== row.id)])
          }
          anchor={offer.id === focusOfferId ? scrollTo : undefined}
          L={L}
          t={t}
          locale={locale}
        />
      ))}
    </>
  );
}

function OfferCard(props: {
  offer: GuestOffer;
  canOrder: boolean;
  open: boolean;
  onToggle: () => void;
  requests: GuestRequest[];
  onRequestChanged: (row: GuestRequest) => void;
  anchor?: (el: HTMLElement | null) => void;
  L: Localize;
  t: Translate;
  locale: string;
}) {
  const {
    offer,
    canOrder,
    open,
    onToggle,
    requests,
    onRequestChanged,
    anchor,
    L,
    t,
    locale,
  } = props;

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [slotStart, setSlotStart] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiKey | null>(null);
  /**
   * One key per confirm ACTION — held across every retry of that same order,
   * and DROPPED the moment the order changes.
   *
   * Both halves matter. Keeping it makes a double tap (or a retry after a lost
   * response) replay the first row instead of stacking a second order. Dropping
   * it on any edit is what stops the opposite failure: a guest whose confirm
   * failed, who then picks a different hour and presses again, would otherwise
   * send a DIFFERENT order under the same key — and if the first attempt had
   * actually reached the server, the replay would hand them the original row
   * while telling them the amended time was booked. A changed order is a
   * genuinely different order, which is the rule the television states too.
   */
  const keyRef = useRef("");
  useEffect(() => {
    keyRef.current = "";
  }, [open, offer.id, quantities, slotStart, note]);

  const free = useMemo(
    () => byDate(offer.slots.filter((s) => s.remaining > 0)),
    [offer.slots]
  );
  // Asked of the OFFER, not of `slots.length`: an empty list is what an `asap`
  // booking looks like AND what a slotted one looks like when every hour has
  // expanded away. Confusing the two lets Confirm through into a 422 the guest
  // cannot act on.
  const needsSlot = offer.booksHours;
  const canConfirm = !busy && (!needsSlot || slotStart !== null);
  const total = totalOf(offer, quantities);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const selection = offer.items
      .map((item) => ({ itemId: item.id, quantity: quantities[item.id] ?? 0 }))
      .filter((s) => s.quantity > 0);
    if (!keyRef.current) keyRef.current = newIdempotencyKey();
    const result = await requestOffer(offer.id, {
      idempotencyKey: keyRef.current,
      ...(selection.length ? { selection } : {}),
      ...(slotStart ? { slotStart } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setBusy(false);
    if (!result.ok) {
      setError(errorKey(result.code));
      return;
    }
    onRequestChanged(result.request);
    setQuantities({});
    setSlotStart(null);
    setNote("");
    onToggle();
    track("click", `offer_confirm:${offer.id}`);
  };

  const withdraw = async (id: string) => {
    setBusy(true);
    const result = await cancelOfferRequest(id);
    setBusy(false);
    if (result.ok) onRequestChanged(result.request);
    else setError(errorKey(result.code));
  };

  return (
    <section className="card card--offer" ref={anchor}>
      {offer.imageUrl && (
        <img className="offer__img" src={offer.imageUrl} alt="" />
      )}
      <div className="offer__body">
        <h3 className="card__title">{L(offer.title)}</h3>
        <p className="card__body">{L(offer.body)}</p>
        <div className="offer__meta">
          {offer.price && (
            <span className="offer__price">
              {formatMoney(offer.price, locale)}
            </span>
          )}
          {offer.fulfil && (
            <span className="offer__tag">
              {t(offer.fulfil === "deliver" ? "offer_deliver" : "offer_desk")}
            </span>
          )}
        </div>

        {/* What this room already asked for. Read-only history - the phone
            cannot be told when it changes, so this is what was true when the
            page loaded. */}
        {requests.length > 0 && (
          <ul className="offer__requests">
            {requests.map((r) => (
              <li key={r.id} className="offer__request">
                <span className={`offer__status offer__status--${r.status}`}>
                  {t(STATUS_KEY[r.status])}
                </span>
                {r.slotStart && (
                  <span className="offer__when">
                    {r.slotStart.replace("T", " ")}
                  </span>
                )}
                {r.status === "pending" && (
                  <button
                    className="offer__link"
                    disabled={busy}
                    onClick={() => withdraw(r.id)}
                  >
                    {t("offer_cancel")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Outside the panel, deliberately: the Cancel button above is
            reachable with the panel CLOSED (the normal state after ordering),
            and an error rendered inside it would leave a failed withdrawal
            looking like nothing happened at all. */}
        {error && <p className="offer__error">{t(error)}</p>}

        {/* A promotion has no confirm step (§2), so it ends here - exactly as
            it does on the television. */}
        {offer.requestable &&
          (canOrder ? (
            <button
              className="offer__cta"
              onClick={() => {
                onToggle();
                if (!open) track("click", `offer_open:${offer.id}`);
              }}
            >
              {open
                ? t("offer_close")
                : t(offer.kind === "booking" ? "offer_book" : "offer_order")}
            </button>
          ) : (
            // Property-scope session: no room, so nothing could be delivered
            // or charged. Say why rather than drawing a button that refuses.
            <p className="offer__note">{t("offer_ask_reception")}</p>
          ))}

        {offer.requestable && canOrder && open && (
          <div className="offer__panel">
            {offer.items.length > 0 && (
              <ul className="offer__items">
                {offer.items.map((item) => {
                  const qty = quantities[item.id] ?? 0;
                  const set = (n: number) =>
                    setQuantities((q) => ({
                      ...q,
                      [item.id]: Math.max(0, Math.min(9, n)),
                    }));
                  return (
                    <li className="offer__item" key={item.id}>
                      <OfferIcon name={item.icon} />
                      <span className="offer__label">{L(item.label)}</span>
                      <span className="offer__itemprice">
                        {item.price
                          ? formatMoney(item.price, locale)
                          : t("offer_included")}
                      </span>
                      <span className="offer__stepper">
                        <button
                          aria-label="−"
                          disabled={qty === 0}
                          onClick={() => set(qty - 1)}
                        >
                          −
                        </button>
                        <span className="offer__qty">{qty}</span>
                        <button aria-label="+" onClick={() => set(qty + 1)}>
                          +
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {needsSlot && (
              <div className="offer__slots">
                <div className="offer__slotlabel">
                  {free.length ? t("offer_pick_time") : t("offer_no_times")}
                </div>
                {free.map(([date, slots]) => (
                  <div className="offer__day" key={date}>
                    <div className="offer__date">{date}</div>
                    <div className="offer__times">
                      {slots.map((slot) => (
                        <button
                          key={slot.start}
                          className={
                            slotStart === slot.start
                              ? "offer__time offer__time--on"
                              : "offer__time"
                          }
                          onClick={() => setSlotStart(slot.start)}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <label className="offer__notefield">
              <span className="offer__slotlabel">{t("offer_note")}</span>
              <textarea
                value={note}
                rows={2}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>

            {/* Charge-to-room, stated plainly. No payment-method picker: the
                demo has one method, and one option is not a choice. */}
            {total && (
              <div className="offer__total">
                <span>{t("offer_total")}</span>
                <strong>{formatMoney(total, locale)}</strong>
              </div>
            )}
            {total && (
              <p className="offer__note">{t("offer_charged_to_room")}</p>
            )}

            <button
              className="offer__cta offer__cta--confirm"
              disabled={!canConfirm}
              onClick={confirm}
            >
              {busy ? t("offer_sending") : t("offer_confirm")}
            </button>
          </div>
        )}

        {offer.terms && <p className="offer__terms">{L(offer.terms)}</p>}
      </div>
    </section>
  );
}
